import React, { useState, useEffect } from 'react';
import { X, Loader2, CheckCircle, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useData } from '../contexts/DataContext';
import { lockScroll, unlockScroll } from '../services/scrollLock';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type FormErrors = {
  name?: string;
  email?: string;
  organization?: string;
  projectInterest?: string;
};

export const RequestDeckModal = () => {
  const { projects } = useData();
  const [isOpen, setIsOpen] = useState(false);
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [errors, setErrors] = useState<FormErrors>({});

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    projectInterest: '',
    message: ''
  });

  useEffect(() => {
    const handleOpen = (e: any) => {
      setIsOpen(true);
      if (e.detail?.projectName) {
        setFormData(prev => ({ ...prev, projectInterest: e.detail.projectName }));
      }
    };

    window.addEventListener('open-deck-modal', handleOpen);
    return () => window.removeEventListener('open-deck-modal', handleOpen);
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    lockScroll();
    return () => unlockScroll();
  }, [isOpen]);

  // Hide the floating chat launcher/widget while this modal is open (all
  // viewports). The ChatWidget listens for these same events, so we reuse that
  // mechanism instead of relying on brittle CSS selectors.
  useEffect(() => {
    if (!isOpen) return undefined;
    window.dispatchEvent(new Event('modal-opened'));
    return () => {
      window.dispatchEvent(new Event('modal-closed'));
    };
  }, [isOpen]);

  // Update a single field and clear its inline error as the user edits/selects.
  const updateField = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => {
      const key = field as keyof FormErrors;
      return prev[key] ? { ...prev, [key]: undefined } : prev;
    });
  };

  // Custom validation. Native browser bubbles are disabled via `noValidate` on
  // the form, so this is the single source of truth for field errors.
  const validate = (): FormErrors => {
    const next: FormErrors = {};
    if (!formData.name.trim()) next.name = 'Please enter your name.';
    if (!formData.email.trim()) {
      next.email = 'Please enter your email.';
    } else if (!EMAIL_REGEX.test(formData.email.trim())) {
      next.email = 'Please enter a valid email address.';
    }
    if (!formData.organization.trim()) next.organization = 'Please enter your organization.';
    if (!formData.projectInterest) next.projectInterest = 'Please select a project.';
    return next;
  };

  const closeModal = () => {
    setIsOpen(false);
    setErrors({});
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});

    setStatus('sending');

    try {
      const payload = new FormData();
      payload.append('investor-name', formData.name);
      payload.append('investor-email', formData.email);
      payload.append('company', formData.organization);
      payload.append('project-interest', formData.projectInterest);
      payload.append('message', formData.message || 'Requesting Deck Access');

      const response = await fetch('https://formspree.io/f/mkoezkng', {
        method: 'POST',
        body: payload,
        headers: { Accept: 'application/json' }
      });

      if (!response.ok) {
        throw new Error('Formspree request failed');
      }

      setStatus('success');
      setTimeout(() => {
        setIsOpen(false);
        setStatus('idle');
        setErrors({});
        setFormData({ name: '', email: '', organization: '', projectInterest: '', message: '' });
      }, 2000);
    } catch (error) {
      console.error(error);
      setStatus('error');
    }
  };

  // Shared field styling; the border flips to a red highlight when invalid.
  const fieldBase =
    'w-full bg-[#18181b] border rounded-lg px-3 py-2.5 text-white text-xs md:text-sm focus:outline-none transition-colors';
  const borderClass = (hasError: boolean) =>
    hasError ? 'border-red-500/50 focus:border-red-500/70' : 'border-white/10 focus:border-blue-500/50';

  return (
    <AnimatePresence>
      {isOpen && (
    <div key="deck-modal" className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <motion.div
        className="absolute inset-0 bg-black/80 backdrop-blur-md"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
        onClick={closeModal}
      />

      {/* Modal Container */}
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 8 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
        className="relative w-full max-w-[460px] bg-[#09090b] border border-white/10 rounded-3xl shadow-2xl flex flex-col max-h-[85vh] overflow-hidden"
      >

        {/* Close Button */}
        <button
          onClick={closeModal}
          className="absolute top-3 right-3 z-50 p-2 rounded-full bg-[#18181b] hover:bg-white/10 text-zinc-400 hover:text-white transition-colors border border-white/5"
        >
          <X size={16} />
        </button>

        {/* Scrollable Content — data-lenis-prevent keeps this inner scroll native
            even while Lenis is stopped by the modal lock. */}
        {/* Уменьшил padding (p-5 -> p-4) для компактности на мобилках */}
        <div data-lenis-prevent className="overflow-y-auto p-5 md:p-6 custom-scrollbar">

          {/* Header */}
          <h2 className="text-xl font-semibold text-white mb-1.5 pr-8">Request Investment Deck</h2>
          <p className="text-zinc-400 text-xs mb-5 leading-relaxed">
            Access to detailed investment materials and financials is restricted to accredited investors.
          </p>

          {/* Form - Уменьшил space-y-4 до space-y-3 */}
          <form onSubmit={handleSubmit} action="https://formspree.io/f/mkoezkng" method="POST" noValidate className="space-y-3">

            {/* Row 1: Name & Email */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-[9px] font-bold text-zinc-500 uppercase tracking-wider pl-1">Full Name <span className="text-red-500">*</span></label>
                <input
                  name="investor-name"
                  required
                  value={formData.name}
                  onChange={e => updateField('name', e.target.value)}
                  className={`${fieldBase} ${borderClass(!!errors.name)}`}
                  placeholder="Your name"
                />
                {errors.name && <p className="text-[11px] text-red-400 pl-1 mt-1">{errors.name}</p>}
              </div>
              <div className="space-y-1">
                <label className="text-[9px] font-bold text-zinc-500 uppercase tracking-wider pl-1">Work Email <span className="text-red-500">*</span></label>
                <input
                  name="investor-email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={e => updateField('email', e.target.value)}
                  className={`${fieldBase} ${borderClass(!!errors.email)}`}
                  placeholder="name@fund.com"
                />
                {errors.email && <p className="text-[11px] text-red-400 pl-1 mt-1">{errors.email}</p>}
              </div>
            </div>

            {/* Organization */}
            <div className="space-y-1">
               <label className="text-[9px] font-bold text-zinc-500 uppercase tracking-wider pl-1">Organization / VC Firm <span className="text-red-500">*</span></label>
               <input
                 name="company"
                 required
                 value={formData.organization}
                 onChange={e => updateField('organization', e.target.value)}
                 className={`${fieldBase} ${borderClass(!!errors.organization)}`}
                 placeholder="Your organization"
               />
               {errors.organization && <p className="text-[11px] text-red-400 pl-1 mt-1">{errors.organization}</p>}
            </div>

            {/* Project Selection */}
            <div className="space-y-1">
               <label className="text-[9px] font-bold text-zinc-500 uppercase tracking-wider pl-1">Project of Interest</label>
               <div className="relative">
                 <select
                   name="project-interest"
                   required
                   value={formData.projectInterest}
                   onChange={e => updateField('projectInterest', e.target.value)}
                   className={`${fieldBase} appearance-none cursor-pointer ${borderClass(!!errors.projectInterest)}`}
                 >
                   <option value="" disabled className="text-zinc-500">Select a project</option>
                   {projects.map(p => (
                     <option key={p.id} value={p.title || p.name} className="bg-zinc-900">{p.title || p.name}</option>
                   ))}
                   <option value="General" className="bg-zinc-900">General Inquiry</option>
                 </select>
                 <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
               </div>
               {errors.projectInterest && <p className="text-[11px] text-red-400 pl-1 mt-1">{errors.projectInterest}</p>}
            </div>

            {/* Message */}
            <div className="space-y-1">
               <label className="text-[9px] font-bold text-zinc-500 uppercase tracking-wider pl-1">Message (Optional)</label>
               <textarea
                 name="message"
                 rows={2}
                 value={formData.message}
                 onChange={e => updateField('message', e.target.value)}
                 className="w-full bg-[#18181b] border border-white/10 rounded-lg px-3 py-2.5 text-white text-xs md:text-sm focus:border-blue-500/50 focus:outline-none resize-none transition-colors"
                 placeholder="Additional context..."
               />
            </div>

            {/* Submit Button */}
            <div className="pt-2">
              <button
                type="submit"
                disabled={status === 'sending' || status === 'success'}
                className="w-full bg-white hover:bg-zinc-200 text-black font-bold rounded-full py-3 transition-all flex items-center justify-center gap-2 active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed shadow-lg shadow-white/5 text-sm"
              >
                {status === 'sending' ? (
                  <Loader2 className="animate-spin" size={18} />
                ) : status === 'success' ? (
                  <>Request Sent <CheckCircle size={18} /></>
                ) : (
                  'Request Access'
                )}
              </button>

              {status === 'success' && (
                <p className="text-[11px] text-emerald-400 text-center pt-3">
                  Request sent successfully. We will get back to you shortly.
                </p>
              )}
              <p className="text-[10px] text-zinc-400 text-center pt-2 pb-0">
                By submitting, you confirm you are an accredited investor.
              </p>
            </div>

          </form>
        </div>
      </motion.div>
    </div>
      )}
    </AnimatePresence>
  );
};
